import { create } from 'zustand';
import { supabase } from './supabase';
import type { OneOnOneMeeting, MeetingAgendaItem, TaskOwner } from './types';
import { useAuth } from './auth';

interface MeetingStore {
  meetings: OneOnOneMeeting[];
  agendaItems: { [meetingId: string]: MeetingAgendaItem[] };
  isLoading: boolean;
  error: string | null;
  
  // Meeting actions
  fetchMeetings: () => Promise<void>;
  createMeeting: (meeting: Omit<OneOnOneMeeting, 'id' | 'created_at'>) => Promise<OneOnOneMeeting>;
  updateMeeting: (id: string, updates: Partial<OneOnOneMeeting>) => Promise<void>;
  deleteMeeting: (id: string) => Promise<void>;
  
  // Agenda actions
  fetchAgendaItems: (meetingId: string) => Promise<MeetingAgendaItem[]>;
  createAgendaItem: (item: Omit<MeetingAgendaItem, 'id' | 'created_at'>) => Promise<MeetingAgendaItem>;
  updateAgendaItem: (id: string, updates: Partial<MeetingAgendaItem>) => Promise<void>;
  deleteAgendaItem: (id: string, meetingId: string) => Promise<void>;
}

export const useMeetingStore = create<MeetingStore>((set, get) => ({
  meetings: [],
  agendaItems: {},
  isLoading: false,
  error: null,

  fetchMeetings: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user);
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // First get the task owner ID for this user
      const { data: taskOwner } = await supabase
        .from('task_owners')
        .select('id')
        .eq('user_id', user.id)
        .single();

      console.log('Task owner:', taskOwner);

      if (!taskOwner) {
        console.log('No task owner found for user');
        set({ meetings: [] });
        return;
      }

      const { data: meetings, error } = await supabase
        .from('one_on_one_meetings')
        .select(`
          *,
          team_member:team_member_id(id, name, email, organization),
          facilitator:facilitator_id(id, name, email, organization)
        `)
        .eq('facilitator_id', taskOwner.id)
        .order('meeting_date', { ascending: false });

      console.log('Meetings query result:', { meetings, error });
      
      if (error) {
        console.error('Error fetching meetings:', error);
        throw error;
      }
      
      set({ meetings: meetings || [] });
      console.log('Set meetings in store:', meetings);
    } catch (error) {
      console.error('Error in fetchMeetings:', error);
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  createMeeting: async (meeting) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('one_on_one_meetings')
        .insert([meeting])
        .select()
        .single();

      if (error) {
        console.error('Error creating meeting:', error);
        throw error;
      }
      
      await get().fetchMeetings();
      return data;
    } catch (error) {
      console.error('Error creating meeting:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateMeeting: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('one_on_one_meetings')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating meeting:', error);
        throw error;
      }
      
      await get().fetchMeetings();
    } catch (error) {
      console.error('Error updating meeting:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteMeeting: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('one_on_one_meetings')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting meeting:', error);
        throw error;
      }
      
      await get().fetchMeetings();
    } catch (error) {
      console.error('Error deleting meeting:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchAgendaItems: async (meetingId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('meeting_agenda_items')
        .select('*')
        .eq('meeting_id', meetingId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching agenda items:', error);
        throw error;
      }

      const items = data || [];
      set(state => ({
        agendaItems: {
          ...state.agendaItems,
          [meetingId]: items
        }
      }));
      return items;
    } catch (error) {
      console.error('Error fetching agenda items:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  createAgendaItem: async (item) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('meeting_agenda_items')
        .insert([item])
        .select()
        .single();

      if (error) {
        console.error('Error creating agenda item:', error);
        throw error;
      }

      if (data) {
        set(state => ({
          agendaItems: {
            ...state.agendaItems,
            [data.meeting_id]: [
              ...(state.agendaItems[data.meeting_id] || []),
              data
            ]
          }
        }));
      }
      
      return data;
    } catch (error) {
      console.error('Error creating agenda item:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateAgendaItem: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('meeting_agenda_items')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating agenda item:', error);
        throw error;
      }
      
      // Refresh the agenda items for this meeting
      if (updates.meeting_id) {
        await get().fetchAgendaItems(updates.meeting_id);
      }
    } catch (error) {
      console.error('Error updating agenda item:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteAgendaItem: async (id, meetingId) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('meeting_agenda_items')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting agenda item:', error);
        throw error;
      }

      set(state => ({
        agendaItems: {
          ...state.agendaItems,
          [meetingId]: state.agendaItems[meetingId]?.filter(item => item.id !== id) || []
        }
      }));
    } catch (error) {
      console.error('Error deleting agenda item:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  }
}));
